import React from "react";
import { NPS } from "../../types/nps";
import BaseDashboardCard from "../BaseDashboardCard";
import Box from "../Box";
import NPSPieChart from "../NPSPieChart";
import { CTag, CText } from "@carrefour/design-system-components-react";
import styled from "styled-components";
import SelectOption from "../SelectOption";
import { Choice } from "../../types/commercial";
import { SingleValue } from "react-select";
import { useSearchParams } from "react-router-dom";
import { eventTrack } from "../../utils/gtag";
import { useTranslation } from "react-i18next";
import { formatPercentage } from "../../utils/numberUtils";
import { useRange } from "../../context/rangeContext";
import { Range } from "../../utils/range";

const BoxCustom = styled(Box)<{ gap: string }>`
  gap: ${(props) => props.gap};
  display: flex;
  align-items: center;
`;
const BoxRectangle = styled(Box)<{ color: string }>`
  /* BoxRectangle 2179 */
  width: 16px;
  height: 16px;

  background: ${(props) => props.color};
  border-radius: 2px;

  /* Inside auto layout */
  flex: none;
  order: 0;
  flex-grow: 0;
`;
const Text = styled(CText)`
  font-family: system-ui;
`;
const BoxSelect = styled(Box)`
  width: 60%;
  @media only screen and (max-width: 600px) {
    width: 100%;
  }
`;
const Tag = styled(CTag)`
  font-family: system-ui;
`;
type NPSCardProps = {
  title: string;
  npsData: NPS;
  options?: Choice[];
  param?: string;
  hideRangeSelect?: boolean;
  rangeLabel?: string;
  textServices?: string;
  isUnavailable: boolean;
  isLoading: boolean;
};

const NPSCard = ({
  title,
  npsData,
  textServices,
  options,
  param,
  hideRangeSelect = false,
  rangeLabel,
  ...rest
}: NPSCardProps) => {
  const respondentsCount = npsData
    ? npsData!.promoters + npsData!.detractors + npsData!.neutrals
    : 0;

  const promotersPercentage =
    npsData && respondentsCount
      ? (npsData!.promoters / respondentsCount) * 100
      : 0;
  const detractorsPercentage =
    npsData && respondentsCount
      ? (npsData!.detractors / respondentsCount) * 100
      : 0;
  const neutralsPercentage =
    npsData && respondentsCount
      ? (npsData!.neutrals / respondentsCount) * 100
      : 0;

  const {t} =useTranslation();        
 
  const responseText = respondentsCount < 2 ?  t('operationnel.repondant') :  t('operationnel.repondants');

  const [searchParams, setSearchParams] = useSearchParams();
  const { setKpiRange, getKpiRange } = useRange();

  const getDefaultValueParams = () => {
    if (!param) return (options?.[0]?.value as string) ?? "";
    const defaultParam = param === "npsStore"
        ? (options?.[1]?.value as string)
        : (options?.[0]?.value as string)

    const range = getKpiRange(param);
    const val = range ? range : defaultParam;

    searchParams.set(
      param,
      val as string
    );
    setSearchParams(searchParams);
    // first tracking
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: val
    });
    return val
  };

  const showRangeSelect = !hideRangeSelect && Boolean(options && options.length > 0);
  const npsParam = showRangeSelect
    ? (param ? searchParams.get(param) ?? getDefaultValueParams() : (options?.[0]?.value ?? ""))
    : "";

  const handleChange = (choice: SingleValue<Choice>) => {
    const newValue = choice?.value as string;

    if (newValue === npsParam) {
      return;
    }
    if (param) {
      searchParams.set(param, newValue as string);
      setSearchParams(searchParams);
      setKpiRange(param, newValue as Range);
      //tracking changed
      eventTrack({
        eventName: "interaction_metric",
        actionName: "filter applied",
        key1: "metric_name",
        value1: title,
        key2: "filter_name",
        value2: newValue,
      });
    }
  };

  const displayTitle = rangeLabel ? `${title} - ${rangeLabel}` : title;

  return (
    <BaseDashboardCard
      hasModal={true}
      title={displayTitle}
      openInformationModal={() => null}
      size="small"
      importedDateTime={npsData?.importedDateTime}
      option={
        showRangeSelect && options ? (
          <BoxSelect py={1}>
            <SelectOption
              list={options}
              value={npsParam}
              handleChange={handleChange}
            />
          </BoxSelect>
        ) : null
      }
      {...rest}
    >
      <Box
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
        py={4}
      >
        <NPSPieChart
          promotersPercentage={promotersPercentage}
          detractorsPercentage={detractorsPercentage}
          neutralsPercentage={neutralsPercentage}
        />

        <BoxCustom
          display={"flex"}
          alignItems={"center"}
          justifyContent={"center"}
          gap="3rem"
          py={2}
        >
          <BoxCustom gap="4px" flexDirection={"column"}>
            <BoxRectangle color={"#00bf7d"} />
            <Text tagStyle="p" size={"s"} bold>
              {`${formatPercentage(promotersPercentage)}`}
            </Text>
          </BoxCustom>
          <BoxCustom gap="4px" flexDirection={"column"}>
            <BoxRectangle color={"#E5E9F0"} />
            <Text tagStyle="p" size={"s"} bold>
              {`${formatPercentage(neutralsPercentage)}`}
            </Text>
          </BoxCustom>
          <BoxCustom gap="4px" flexDirection={"column"}>
            <BoxRectangle color={"#E74429"} />
            <Text tagStyle="p" size={"s"} bold>
              {`${formatPercentage(detractorsPercentage)}`}
            </Text>
          </BoxCustom>
        </BoxCustom>
        <Box display={"flex"} alignItems={"center"}>
          <Tag icon={"users"} status={"success"} hasBorder={false} size={"l"}>
            {`${respondentsCount} ${responseText}`}
          </Tag>
        </Box>
      </Box>
    </BaseDashboardCard>
  );
};

export default NPSCard;
